import React, { useState, useEffect, useCallback , useMemo } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, addDays, subDays, startOfWeek, parseISO, isToday, isBefore } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Users, AlertTriangle, CheckCircle2, Calendar, ChevronLeft, ChevronRight,
  Clock, XCircle, Coffee, BookOpen, Briefcase, UserCheck, Eye, FileDown,
  Plus, X, Search, ClipboardCheck, Activity, MapPin, Ban
} from 'lucide-react';
import { getAuthToken } from '../lib/authToken';

import { ACTIVITY_TYPES, ALL_ACTIVITY_TYPES } from '../lib/activites';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUT_CONFIG = {
  en_formation: { label: 'Formation', color: 'bg-blue-500', textColor: 'text-white', icon: BookOpen },
  ...Object.fromEntries(ALL_ACTIVITY_TYPES.map(a => [a.value, { label: a.short, color: a.bg, textColor: 'text-white', icon: Briefcase, fullLabel: a.label }])),
  non_remunere: { label: 'Non rem.', color: 'bg-slate-400', textColor: 'text-white', icon: Ban },
  disponible: { label: '', color: 'bg-slate-50', textColor: 'text-slate-300', icon: null },
  delegation_commerciale: { label: 'Deleg. Co', color: 'bg-teal-200', textColor: 'text-teal-800', icon: Briefcase },
  delegation_pedagogique: { label: 'Deleg. Ped', color: 'bg-indigo-200', textColor: 'text-indigo-800', icon: BookOpen },
  delegation_admin: { label: 'Admin', color: 'bg-cyan-200', textColor: 'text-cyan-800', icon: ClipboardCheck },
  non_affecte: { label: '', color: 'bg-red-50', textColor: 'text-red-300', icon: AlertTriangle },
};

const CONTRAT_BADGE = {
  vacataire: { label: 'Vac', color: 'bg-amber-100 text-amber-700' },
  cdd: { label: 'CDD', color: 'bg-blue-100 text-blue-700' },
  cdi: { label: 'CDI', color: 'bg-emerald-100 text-emerald-700' },
};

const SOUS_CATEGORIES_HP = {
  hors_production: [
    { value: 'mission', label: 'Mission' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'administratif', label: 'Administratif' },
    { value: 'autre', label: 'Autre' },
  ],
  developpement_pedagogique: [
    { value: 'preparation_cours', label: 'Preparation cours' },
    { value: 'formation_interne', label: 'Formation interne' },
    { value: 'reunion_pedagogique', label: 'Reunion pedagogique' },
    { value: 'veille', label: 'Veille' },
  ],
  non_remunere: [
    { value: 'intercontrat', label: 'Intercontrat' },
    { value: 'autre', label: 'Autre' },
  ],
};

const JOURS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'];

export const PortailResponsablePage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [nbWeeks, setNbWeeks] = useState(2);
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog state for assigning HP
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignData, setAssignData] = useState({
    formateur_id: '', formateur_nom: '', date: '',
    type_affectation: 'developpement_projet', sous_categorie: '', note: '',
  });

  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  const dateDebut = format(weekStart, 'yyyy-MM-dd');
  const dateFin = format(addDays(weekStart, nbWeeks * 7 - 3), 'yyyy-MM-dd');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/portail/responsable/tableau-bord?date_debut=${dateDebut}&date_fin=${dateFin}`,
        { headers }
      );
      setData(res.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [dateDebut, dateFin, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const goBack = () => setWeekStart(subDays(weekStart, 7));
  const goForward = () => setWeekStart(addDays(weekStart, 7));
  const goToday = () => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  // Open assign dialog
  const openAssign = (formateurId, formateurNom, date) => {
    setAssignData({
      formateur_id: formateurId, formateur_nom: formateurNom, date,
      type_affectation: 'developpement_projet', sous_categorie: '', note: '',
    });
    setAssignDialog(true);
  };

  // Save HP affectation
  const handleSaveAffectation = async () => {
    try {
      await axios.post(`${API_URL}/requetes/affectation-hp`, {
        formateur_id: assignData.formateur_id,
        date: assignData.date,
        type_affectation: assignData.type_affectation,
        sous_categorie: assignData.sous_categorie,
        note: assignData.note,
      }, { headers });
      toast.success('Affectation enregistree');
      setAssignDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  // Delete HP affectation
  const handleDeleteAffectation = async (formateurId, date) => {
    try {
      await axios.delete(`${API_URL}/requetes/affectation-hp/${formateurId}/${date}`, { headers });
      toast.success('Affectation supprimee');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const dates = data?.dates || [];
  const formateurs = (data?.formateurs || []).filter(f =>
    !searchTerm || `${f.nom} ${f.prenom}`.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const alertes = data?.alertes || {};

  // Group dates by week for header
  const weekGroups = [];
  let currentGroup = null;
  for (const d of dates) {
    const dt = parseISO(d);
    const ws = format(startOfWeek(dt, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    if (!currentGroup || currentGroup.weekStart !== ws) {
      currentGroup = { weekStart: ws, dates: [] };
      weekGroups.push(currentGroup);
    }
    currentGroup.dates.push(d);
  }

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="portail-responsable">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
              Portail Responsable Pedagogique
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Suivi des affectations et activites des formateurs</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(nbWeeks)} onValueChange={(v) => setNbWeeks(Number(v))}>
              <SelectTrigger className="w-28 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 semaine</SelectItem>
                <SelectItem value="2">2 semaines</SelectItem>
                <SelectItem value="3">3 semaines</SelectItem>
                <SelectItem value="4">4 semaines</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Alert cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formateurs.length}</p>
                <p className="text-xs text-slate-500">Collaborateurs actifs</p>
              </div>
            </CardContent>
          </Card>
          <Card className={`border-0 shadow-sm ${alertes.non_affectes > 0 ? 'bg-gradient-to-br from-red-50 to-white ring-1 ring-red-200' : 'bg-gradient-to-br from-emerald-50 to-white'}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${alertes.non_affectes > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                <AlertTriangle className={`w-5 h-5 ${alertes.non_affectes > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{alertes.non_affectes || 0}</p>
                <p className="text-xs text-slate-500">Jours non affectes (passes)</p>
              </div>
            </CardContent>
          </Card>
          <Card className={`border-0 shadow-sm ${alertes.non_valides > 0 ? 'bg-gradient-to-br from-amber-50 to-white ring-1 ring-amber-200' : 'bg-gradient-to-br from-emerald-50 to-white'}`}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${alertes.non_valides > 0 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                <ClipboardCheck className={`w-5 h-5 ${alertes.non_valides > 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{alertes.non_valides || 0}</p>
                <p className="text-xs text-slate-500">Activites non validees (passes)</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Navigation + search */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goBack} className="h-8">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToday} className="h-8 text-xs">
              Aujourd'hui
            </Button>
            <Button variant="outline" size="sm" onClick={goForward} className="h-8">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium text-slate-700 ml-2">
              {format(parseISO(dateDebut), 'd MMM', { locale: fr })} — {format(parseISO(dateFin), 'd MMM yyyy', { locale: fr })}
            </span>
          </div>
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              placeholder="Filtrer un formateur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-xs"
              data-testid="search-formateur"
            />
          </div>
        </div>

        {/* Main grid */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="py-12 flex justify-center">
                <div className="animate-spin w-6 h-6 border-3 border-indigo-600 border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="rp-grid">
                  {/* Header: week labels */}
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="sticky left-0 z-10 bg-slate-100 px-3 py-1.5 text-left text-[10px] font-medium text-slate-500 uppercase tracking-wide w-40 min-w-[160px]">
                        Collaborateur
                      </th>
                      {weekGroups.map((wg, idx) => (
                        <th
                          key={wg.weekStart}
                          colSpan={wg.dates.length}
                          className="px-1 py-1 text-center text-[9px] font-semibold text-slate-400 uppercase tracking-wide border-l border-slate-200"
                        >
                          Sem. {format(parseISO(wg.weekStart), 'w', { locale: fr })}
                        </th>
                      ))}
                    </tr>
                    <tr className="bg-slate-50 border-b">
                      <th className="sticky left-0 z-10 bg-slate-50 px-3 py-1 text-left" />
                      {dates.map(d => {
                        const dt = parseISO(d);
                        const today = isToday(dt);
                        return (
                          <th
                            key={d}
                            className={`px-0.5 py-1 text-center min-w-[52px] border-l border-slate-100 ${today ? 'bg-indigo-100' : ''}`}
                          >
                            <div className={`text-[9px] font-medium ${today ? 'text-indigo-700' : 'text-slate-500'}`}>
                              {JOURS_FR[dt.getDay() - 1]}
                            </div>
                            <div className={`text-[10px] font-bold ${today ? 'text-indigo-800' : 'text-slate-700'}`}>
                              {format(dt, 'd')}
                            </div>
                            <div className="text-[8px] text-slate-400 capitalize">
                              {format(dt, 'MMM', { locale: fr })}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {formateurs.map(f => (
                      <tr key={f.formateur_id} className="hover:bg-slate-50/50 transition-colors" data-testid={`rp-row-${f.formateur_id}`}>
                        <td className="sticky left-0 z-10 bg-white px-3 py-1.5 border-r border-slate-100">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-bold shrink-0">
                              {(f.nom || '')[0]}{(f.prenom || '')[0]}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1">
                                <p className="text-xs font-medium text-slate-800 truncate">{f.nom} {f.prenom}</p>
                                {f.type_contrat && CONTRAT_BADGE[f.type_contrat] && (
                                  <span className={`text-[8px] px-1 py-0.5 rounded font-semibold shrink-0 ${CONTRAT_BADGE[f.type_contrat].color}`}>
                                    {CONTRAT_BADGE[f.type_contrat].label}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 mt-0.5">
                                {(f.fonctions || []).filter(fn => fn.toLowerCase() !== 'formateur').map(fn => (
                                  <span key={fn} className="text-[7px] px-1 py-0 rounded bg-slate-100 text-slate-500">
                                    {fn === 'commercial' || fn === 'Commercial' ? 'Com.' :
                                     fn === 'referent_pedagogique' || fn === 'Referent pedagogique' ? 'Ref. Ped.' :
                                     fn === 'administrateur' || fn === 'Administrateur' ? 'Admin' : fn}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                        {dates.map(d => {
                          const jour = f.jours?.[d];
                          if (!jour) return <td key={d} className="border-l border-slate-100" />;
                          const cfg = STATUT_CONFIG[jour.statut] || STATUT_CONFIG.non_affecte;
                          const dt = parseISO(d);
                          const isPast = isBefore(dt, new Date()) && !isToday(dt);
                          const isNonAffecte = jour.statut === 'non_affecte';
                          const isDisponible = jour.statut === 'disponible';
                          const isDelegation = ['delegation_commerciale', 'delegation_pedagogique', 'delegation_admin'].includes(jour.statut);
                          const isNonValide = jour.statut === 'en_formation' && !jour.valide && isPast;

                          return (
                            <td
                              key={d}
                              className={`px-0.5 py-1 text-center border-l border-slate-100 min-w-[52px] ${isToday(dt) ? 'bg-indigo-50/30' : ''}`}
                            >
                              {isNonAffecte ? (
                                <button
                                  onClick={() => openAssign(f.formateur_id, `${f.nom} ${f.prenom}`, d)}
                                  className={`w-full h-7 rounded text-[8px] font-medium transition-colors ${
                                    isPast
                                      ? 'bg-red-100 text-red-500 hover:bg-red-200 ring-1 ring-red-200'
                                      : 'bg-red-50 text-red-300 hover:bg-indigo-50 hover:text-indigo-400'
                                  }`}
                                  title={`Affecter ${f.nom} ${f.prenom} le ${format(dt, 'dd/MM/yyyy')}`}
                                  data-testid={`assign-${f.formateur_id}-${d}`}
                                >
                                  {isPast ? <AlertTriangle className="w-3 h-3 mx-auto" /> : <Plus className="w-3 h-3 mx-auto" />}
                                </button>
                              ) : isDisponible ? (
                                <button
                                  onClick={() => openAssign(f.formateur_id, `${f.nom} ${f.prenom}`, d)}
                                  className="w-full h-7 rounded text-[8px] font-medium bg-slate-50 text-slate-300 hover:bg-indigo-50 hover:text-indigo-400 transition-colors"
                                  title={`Vacataire disponible - Affecter ${f.nom} ${f.prenom}`}
                                >
                                  <Plus className="w-3 h-3 mx-auto opacity-30" />
                                </button>
                              ) : isDelegation ? (
                                <div className="group relative">
                                  <button
                                    onClick={() => openAssign(f.formateur_id, `${f.nom} ${f.prenom}`, d)}
                                    className={`w-full h-7 rounded flex items-center justify-center text-[7px] font-bold ${cfg.color} ${cfg.textColor} hover:opacity-80 transition-opacity`}
                                    title={`${cfg.fullLabel || cfg.label} - Cliquer pour changer`}
                                  >
                                    <span>{cfg.label}</span>
                                  </button>
                                </div>
                              ) : (
                                <div className="group relative">
                                  <div
                                    className={`w-full h-7 rounded flex items-center justify-center text-[8px] font-bold ${cfg.color} ${cfg.textColor} ${isNonValide ? 'ring-2 ring-amber-400' : ''}`}
                                    title={`${cfg.fullLabel || cfg.label}${jour.detail ? ': ' + jour.detail : ''}${isNonValide ? ' (non valide)' : ''}`}
                                  >
                                    {jour.statut === 'en_formation' ? (
                                      <span className="truncate px-0.5">{jour.detail || 'AF'}</span>
                                    ) : (
                                      <span>{cfg.label}</span>
                                    )}
                                    {isNonValide && (
                                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full border border-white" />
                                    )}
                                  </div>
                                  {/* Delete button for HP */}
                                  {jour.hp && (
                                    <button
                                      onClick={() => handleDeleteAffectation(f.formateur_id, d)}
                                      className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                      title="Supprimer"
                                    >
                                      <X className="w-2.5 h-2.5" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {formateurs.length === 0 && (
                      <tr>
                        <td colSpan={dates.length + 1} className="text-center py-8 text-slate-400 text-sm">
                          Aucun formateur
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 px-1">
          {[
            { key: 'en_formation', label: 'En formation' },
            ...ACTIVITY_TYPES.map(a => ({ key: a.value, label: a.label })),
            { key: 'non_remunere', label: 'Non remunere' },
            { key: 'delegation_commerciale', label: 'Deleg. commerciale' },
            { key: 'delegation_pedagogique', label: 'Deleg. pedagogique' },
            { key: 'delegation_admin', label: 'Deleg. admin' },
            { key: 'non_affecte', label: 'Non affecte (CDD/CDI)' },
            { key: 'disponible', label: 'Disponible (vacataire)' },
          ].map(item => (
            <div key={item.key} className="flex items-center gap-1.5">
              <div className={`w-4 h-4 rounded ${STATUT_CONFIG[item.key]?.color || 'bg-gray-200'}`} />
              <span className="text-[10px] text-slate-500">{item.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 ml-2">
            <div className="w-4 h-4 rounded bg-blue-500 ring-2 ring-amber-400" />
            <span className="text-[10px] text-slate-500">Activite non validee</span>
          </div>
        </div>
      </div>

      {/* Assign HP Dialog */}
      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent className="max-w-sm" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              Affecter {assignData.formateur_nom}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            {assignData.date && format(parseISO(assignData.date), 'EEEE d MMMM yyyy', { locale: fr })}
          </p>
          <div className="space-y-3 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Type d'affectation</Label>
              <Select
                value={assignData.type_affectation}
                onValueChange={(v) => setAssignData({ ...assignData, type_affectation: v, sous_categorie: '' })}
              >
                <SelectTrigger className="h-9" data-testid="select-type-affectation">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map(a => <SelectItem key={a.value} value={a.value}><span className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${a.bg}`} />{a.label}</span></SelectItem>)}
                  <SelectItem value="non_remunere">Temps non remunere</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {SOUS_CATEGORIES_HP[assignData.type_affectation] && (
              <div className="space-y-1.5">
                <Label className="text-xs">Sous-categorie</Label>
                <Select
                  value={assignData.sous_categorie || 'none'}
                  onValueChange={(v) => setAssignData({ ...assignData, sous_categorie: v === 'none' ? '' : v })}
                >
                  <SelectTrigger className="h-9" data-testid="select-sous-categorie">
                    <SelectValue placeholder="Choisir..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {SOUS_CATEGORIES_HP[assignData.type_affectation].map(sc => (
                      <SelectItem key={sc.value} value={sc.value}>{sc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Note (optionnel)</Label>
              <Input
                value={assignData.note}
                onChange={(e) => setAssignData({ ...assignData, note: e.target.value })}
                placeholder="Precision..."
                className="h-9"
                data-testid="input-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveAffectation} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-save-affectation">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
